import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import adminApiService from '../services/api';
import { ChatDetail } from '../types';

export default function ChatDetailPage() {
  const { chatId } = useParams<{ chatId: string }>();
  const [chat, setChat] = useState<ChatDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!chatId) return;

    const fetchChatDetail = async () => {
      try {
        setLoading(true);
        setError(null);
        const chatData = await adminApiService.getChatDetail(chatId);
        setChat(chatData);
        setLoading(false);
      } catch (err: any) {
        setError(err.message || 'Failed to load chat details');
        setLoading(false);
      }
    };

    fetchChatDetail();
  }, [chatId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading chat...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="text-red-800">Error: {error}</div>
        <div className="mt-2 flex gap-4 text-sm">
          <Link to="/dashboard" className="text-blue-600 hover:text-blue-800 underline">
            Back to Dashboard
          </Link>
          <Link to="/users" className="text-blue-600 hover:text-blue-800 underline">
            Users
          </Link>
        </div>
      </div>
    );
  }

  if (!chat) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-600">Chat not found</div>
        <div className="mt-4 flex gap-4 text-sm justify-center">
          <Link to="/dashboard" className="text-blue-600 hover:text-blue-800 underline">
            Dashboard
          </Link>
          <Link to="/users" className="text-blue-600 hover:text-blue-800 underline">
            Users
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex gap-4 text-sm">
            <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">
              ← Dashboard
            </Link>
            <Link to="/users" className="text-blue-600 hover:text-blue-800">
              Users
            </Link>
          </div>
          <h1 className="mt-2 text-2xl font-bold text-gray-900">Chat Details</h1>
          <p className="mt-1 text-sm text-gray-500">
            Chat ID: {chat.chatId} • {chat.messages.length} messages
          </p>
        </div>
      </div>

      {/* Chat Info */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <label className="font-medium text-gray-500">User ID</label>
            <p className="mt-1 text-gray-900">{chat.userId || 'N/A'}</p>
          </div>
          <div>
            <label className="font-medium text-gray-500">Machine ID</label>
            <p className="mt-1 text-gray-900">{chat.machineId || 'N/A'}</p>
          </div>
          <div>
            <label className="font-medium text-gray-500">Created</label>
            <p className="mt-1 text-gray-900">
              {new Date(chat.createdAt).toLocaleString()}
            </p>
          </div>
          <div>
            <label className="font-medium text-gray-500">Last Updated</label>
            <p className="mt-1 text-gray-900">
              {new Date(chat.lastUpdated).toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Messages</h2>
        <div className="space-y-4">
          {chat.messages.map((message, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg ${
                message.role === 'user'
                  ? 'bg-blue-50 border-l-4 border-blue-500'
                  : 'bg-gray-50 border-l-4 border-gray-400'
              }`}
            >
              <div className="flex justify-between items-start mb-2">
                <span
                  className={`text-sm font-semibold ${
                    message.role === 'user' ? 'text-blue-800' : 'text-gray-800'
                  }`}
                >
                  {message.role === 'user' ? 'User' : 'Assistant'}
                </span>
                <span className="text-xs text-gray-500">
                  {new Date(message.timestamp).toLocaleString()}
                </span>
              </div>
              <div className="text-sm text-gray-900 whitespace-pre-wrap">
                {message.content}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

